import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Alert,
  Badge,
  Spinner,
} from "react-bootstrap";
import {
  FaUserPlus,
  FaPlus,
  FaTrash,
  FaUser,
  FaGraduationCap,
  FaUserTag,
} from "react-icons/fa";

const AddTeachersModal = ({
  show,
  onHide,
  schoolid,
  rollnumber,
  teacherName,
  countryId,
  addAdditionalColleagues,
  getTeachers, // Add this prop for fetching teachers
  getClasses, // Add this prop for fetching classes
  getRoles, // Add this prop for fetching roles
  currentTeacherId, // Add this prop for current teacher ID
  getSchools, // Add this prop for fetching school data
}) => {
  const [rawTeacherText, setRawTeacherText] = useState("");
  const [parsedTeachers, setParsedTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Parse options
  const [parseMode, setParseMode] = useState("newline");

  // New state for roles, classes, and inviter
  const [roleDefs, setRoleDefs] = useState([]);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [defaultRole, setDefaultRole] = useState("Teacher");
  const [selectedInviter, setSelectedInviter] = useState("ALPACA");
  const [fetchingData, setFetchingData] = useState(false);
  const [schoolCountryId, setSchoolCountryId] = useState(null); // Add state for school's country ID

  // --- Helpers: Academic Year ---
  const getCurrentAcademicYearLabels = () => {
    // Academic year assumed to start in August.
    const now = new Date();
    const month = now.getMonth(); // 0=Jan
    let startYear = now.getFullYear();
    if (month < 7) {
      // before Aug -> academic year started last year
      startYear -= 1;
    }
    const endYear = startYear + 1;
    const short = `${String(startYear % 100).padStart(2, "0")}-${String(
      endYear % 100
    ).padStart(2, "0")}`;
    const long = `${startYear}-${endYear}`;
    return { short, long, startYear, endYear };
  };

  const { short: ayShort, long: ayLong, startYear: ayStart } =
    getCurrentAcademicYearLabels();

  const academicYearFilteredClasses = useMemo(() => {
    if (!Array.isArray(availableClasses) || availableClasses.length === 0) {
      return [];
    }

    const labels = getCurrentAcademicYearLabels();

    const isClassInCurrentAY = (cls) => {
      // Try label-style matches first
      const labelCandidates = [
        cls?.academicYear,
        cls?.academic_year,
        cls?.year,
        cls?.yearLabel,
        cls?.schoolYear,
        cls?.schoolyear,
        cls?.session,
        cls?.academicYearLabel,
        cls?.ay,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());

      const labelMatch = labelCandidates.some(
        (v) =>
          v.includes(labels.short.toLowerCase()) ||
          v.includes(labels.long.toLowerCase())
      );
      if (labelMatch) return true;

      // Try date-based fields
      const dateCandidate =
        cls?.startDate ||
        cls?.start ||
        cls?.yearStart ||
        cls?.start_time ||
        cls?.start_date;
      if (dateCandidate) {
        const d = new Date(dateCandidate);
        if (!Number.isNaN(d.getTime())) {
          const classAYStart = d.getMonth() < 7 ? d.getFullYear() - 1 : d.getFullYear();
          return classAYStart === labels.startYear;
        }
      }

      // If we can't tell, say no (we'll fall back later).
      return false;
    };

    const filtered = availableClasses.filter(isClassInCurrentAY);
    // If filtering produced nothing (fields unknown), fall back to all classes.
    return filtered.length > 0 ? filtered : availableClasses;
  }, [availableClasses]);

  // Fetch school data when modal opens and schoolid is available
  useEffect(() => {
    if (show && (getRoles || getTeachers || getClasses)) {
      fetchSchoolData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, schoolid]);

  // Set current teacher as default inviter if available, otherwise keep ALPACA
  useEffect(() => {
    if (currentTeacherId && availableTeachers.length > 0) {
      const currentTeacher = availableTeachers.find(
        (t) => t.id?.toString() === currentTeacherId?.toString()
      );
      if (currentTeacher) {
        setSelectedInviter(currentTeacher.id.toString());
      }
      // If current teacher not found, leave as ALPACA
    }
    // If no teachers available or no currentTeacherId, leave as ALPACA
  }, [currentTeacherId, availableTeachers]);

  const fetchSchoolData = async () => {
    setFetchingData(true);
    try {
      // Fetch roles - get all roles first, then filter by country
      if (getRoles) {
        const rolesResponse = await getRoles(); // Get all roles without country filter
        const roles =
          rolesResponse?.data && Array.isArray(rolesResponse.data)
            ? rolesResponse.data
            : Array.isArray(rolesResponse)
              ? rolesResponse
              : [];
        const sortedRoles = [...roles].sort((a, b) =>
          (a?.name || "").localeCompare(b?.name || "")
        );
        setRoleDefs(sortedRoles);
      }

      // Fetch school info to get the country ID (similar to EditTeacherModal approach)
      if (getSchools && schoolid) {
        try {
          const schoolsResponse = await getSchools();
          const schools =
            schoolsResponse?.data && Array.isArray(schoolsResponse.data)
              ? schoolsResponse.data
              : Array.isArray(schoolsResponse)
                ? schoolsResponse
                : [];
          const matchingSchool = schools.find((s) => s?.id === schoolid);
          if (matchingSchool) {
            setSchoolCountryId(matchingSchool.countryid ?? matchingSchool.countryId ?? null);
          } else {
            // Fallback to the countryId prop if school lookup fails
            setSchoolCountryId(countryId ?? null);
          }
        } catch (err) {
          // Fallback to the countryId prop if school lookup fails
          setSchoolCountryId(countryId ?? null);
        }
      } else {
        // Fallback to the countryId prop
        setSchoolCountryId(countryId ?? null);
      }

      // Fetch teachers for inviter dropdown
      if (getTeachers && schoolid) {
        console.log("Fetching teachers for schoolid:", schoolid, "vs current user school context");
        const teachersResponse = await getTeachers(schoolid);
        const teachers =
          teachersResponse?.data && Array.isArray(teachersResponse.data)
            ? teachersResponse.data
            : Array.isArray(teachersResponse)
              ? teachersResponse
              : [];
        setAvailableTeachers(Array.isArray(teachers) ? teachers : []);
      }

      // Fetch classes for class assignment
      if (getClasses && schoolid) {
        const classesResponse = await getClasses(schoolid);
        const classes =
          classesResponse?.data && Array.isArray(classesResponse.data)
            ? classesResponse.data
            : Array.isArray(classesResponse)
              ? classesResponse
              : [];
        setAvailableClasses(Array.isArray(classes) ? classes : []);
      }
    } catch (error) {
      console.error("Failed to fetch school data:", error);
      setError("Failed to load school data. Some features may not be available.");
    } finally {
      setFetchingData(false);
    }
  };

  // Update default role when roles and country are available
  useEffect(() => {
    if (roleDefs.length > 0 && schoolCountryId != null) {
      // Filter roles by country and set default role
      const countryRoles = roleDefs.filter(
        (role) =>
          String(role?.countryid ?? role?.countryId ?? "") ===
          String(schoolCountryId)
      );
      const teacherRole = countryRoles.find((role) => role?.name === "Teacher");
      const newDefaultRole = teacherRole
        ? teacherRole.name
        : countryRoles[0]?.name || "Teacher";
      setDefaultRole(newDefaultRole);
    }
  }, [roleDefs, schoolCountryId]);

  useEffect(() => {
    parseTeacherInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTeacherText, parseMode]);

  const parseTeacherInfo = () => {
    if (!rawTeacherText.trim()) {
      setParsedTeachers([]);
      return;
    }

    let separator;
    switch (parseMode) {
      case "comma":
        separator = ",";
        break;
      case "tab":
        separator = "\t";
        break;
      case "newline":
      default:
        separator = "\n";
        break;
    }

    const teacherEntries = rawTeacherText
      .split(separator)
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)
      .map((entry, index) => {
        // Try to parse name and email from the entry
        let name = "";
        let email = "";

        // Check for email patterns
        const emailRegex =
          /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
        const emailMatch = entry.match(emailRegex);

        if (emailMatch) {
          email = emailMatch[0];
          // Remove email from entry to get name
          name = entry.replace(emailMatch[0], "").replace(/[-()]/g, "").trim();
        }

        // Parse first and last name
        const nameParts = name.split(" ").filter((part) => part.trim());
        let firstName = "";
        let lastName = "";

        if (nameParts.length === 1) {
          firstName = nameParts[0];
        } else if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(" ");
        }

        // Validation - both name and email are required
        const hasValidName = firstName.length > 0;
        const hasValidEmail = email.length > 0 && emailRegex.test(email);
        const isValid = hasValidName && hasValidEmail;

        let validationError = "";
        if (!hasValidName && !hasValidEmail)
          validationError = "Name and email are required";
        else if (!hasValidName) validationError = "Name is required";
        else if (!hasValidEmail) validationError = "Valid email is required";

        return {
          id: `temp-${index}`,
          originalEntry: entry,
          firstName: firstName,
          lastName: lastName,
          email: email,
          displayName: `${firstName} ${lastName}`.trim(),
          role: defaultRole, // Use default role
          sharedClassId: null, // Optional field - can be set later if needed
          // Validation
          isValid: isValid,
          validationError: validationError,
        };
      });

    setParsedTeachers(teacherEntries);
  };

  const updateTeacherField = (teacherId, field, value) => {
    setParsedTeachers((prev) =>
      prev.map((teacher) => {
        if (teacher.id === teacherId) {
          const updatedTeacher = { ...teacher, [field]: value };

          // Re-validate when fields change
          if (field === "firstName" || field === "lastName" || field === "email") {
            const emailRegex =
              /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
            const firstNameValue =
              field === "firstName" ? value : teacher.firstName;
            const emailValue = field === "email" ? value : teacher.email;

            const hasValidName = firstNameValue.trim().length > 0;
            const hasValidEmail =
              emailValue.length > 0 && emailRegex.test(emailValue);
            const isValid = hasValidName && hasValidEmail;

            let validationError = "";
            if (!hasValidName && !hasValidEmail)
              validationError = "Name and email are required";
            else if (!hasValidName) validationError = "Name is required";
            else if (!hasValidEmail) validationError = "Valid email is required";

            updatedTeacher.isValid = isValid;
            updatedTeacher.validationError = validationError;

            // Update display name
            if (field === "firstName" || field === "lastName") {
              const firstName =
                field === "firstName" ? value : teacher.firstName;
              const lastName = field === "lastName" ? value : teacher.lastName;
              updatedTeacher.displayName = `${firstName} ${lastName}`.trim();
            }
          }

          return updatedTeacher;
        }
        return teacher;
      })
    );
  };

  const removeTeacher = (teacherId) => {
    setParsedTeachers((prev) =>
      prev.filter((teacher) => teacher.id !== teacherId)
    );
  };

  const addManualTeacher = () => {
    const newTeacher = {
      id: `manual-${Date.now()}`,
      originalEntry: "",
      firstName: "",
      lastName: "",
      email: "",
      displayName: "",
      role: defaultRole,
      sharedClassId: null,
      isValid: false,
      validationError: "Name and email are required",
    };
    setParsedTeachers((prev) => [...prev, newTeacher]);
  };

  const handleAddTeachers = async () => {
    const validTeachers = parsedTeachers.filter((teacher) => teacher.isValid);
    if (validTeachers.length === 0) {
      setError(
        "Please add at least one valid teacher with both name and email."
      );
      return;
    }

    if (!rollnumber || rollnumber.trim() === "") {
      setError(
        "School roll number is required to add teachers. Please ensure a school is selected."
      );
      return;
    }

    if (!teacherName) {
      setError("Teacher name is required.");
      return;
    }

    if (!schoolid) {
      setError("School ID is required. Please ensure a school is selected.");
      return;
    }

    if (!selectedInviter) {
      setError("Please select who is requesting these teacher invitations.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Prepare colleague data for API with new fields
      const colleagues = validTeachers.map((teacher) => ({
        firstName: teacher.firstName,
        lastName: teacher.lastName,
        email: teacher.email,
        roles: teacher.role, // Pass the selected role
        sharedClassId: teacher.sharedClassId || undefined,
      }));

      const selectedTeacherObj = availableTeachers.find(
        (teacher) => teacher.id.toString() === selectedInviter
      );
      const selectedTeacherName = selectedInviter === "ALPACA" 
        ? "ALPACA"
        : selectedTeacherObj
        ? (selectedTeacherObj.name || `${selectedTeacherObj.firstName ?? ""} ${selectedTeacherObj.lastName ?? ""}`.trim())
        : teacherName; // fallback to current user


      // Call the addAdditionalColleagues API function with inviter ID
      await addAdditionalColleagues(
        schoolid,
        rollnumber,
        colleagues,
        selectedTeacherName,
        countryId,
        selectedInviter // Pass the selected inviter ID
      );

      // Success
      setSuccess(
        `Successfully added ${validTeachers.length} teacher(s) as pending colleagues to the school`
      );

      // Reset form
      setRawTeacherText("");
      setParsedTeachers([]);
    } catch (err) {
      setError("Failed to add teachers. Please try again.");
      console.error("Add teachers error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRawTeacherText("");
    setParsedTeachers([]);
    setError("");
    setSuccess("");
    setParseMode("newline");
    setDefaultRole("Teacher");
    setSelectedInviter("ALPACA"); // Reset to ALPACA default
    setSchoolCountryId(null);
    onHide();
  };

  const validTeachersCount = parsedTeachers.filter((t) => t.isValid).length;

  // Get country-filtered roles for display
  const getCountryFilteredRoles = () => {
    if (!schoolCountryId || !roleDefs.length) return roleDefs;
    return roleDefs.filter(
      (role) =>
        String(role?.countryid ?? role?.countryId ?? "") ===
        String(schoolCountryId)
    );
  };

  const countryFilteredRoles = getCountryFilteredRoles();

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      dialogClassName="custom-modal-width"
    >
      <style>
        {`
          .custom-modal-width {
            max-width: 95% !important;
          }
          .custom-modal-width .modal-dialog {
            max-width: 95% !important;
          }
        `}
      </style>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUserPlus className="me-2" />
          Add Teachers to School
          {rollnumber && (
            <small className="text-muted ms-2">(Roll: {rollnumber})</small>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: "700px" }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {fetchingData && (
          <Alert variant="info">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading school data...
          </Alert>
        )}

        {/* Show warning if roll number is missing */}
        {(!rollnumber || rollnumber.trim() === "") && (
          <Alert variant="warning">
            <strong>School roll number is missing!</strong> Please ensure a
            school is properly selected in the main page.
          </Alert>
        )}

        <Row className="h-100">
          {/* Column 1: Input Options & Settings */}
          <Col md={4} className="border-end">
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Add Teachers</h5>
                <Button variant="outline-primary" size="sm" onClick={addManualTeacher}>
                  <FaPlus className="me-1" />
                  Add One
                </Button>
              </div>

              {/* Global Settings */}
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="text-primary mb-3">
                  <FaUserTag className="me-1" />
                  Default Settings
                </h6>

                {/* Default Role Selection */}
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Default Role for New Teachers
                  </Form.Label>
                  <Form.Select
                    value={defaultRole}
                    onChange={(e) => {
                      const newDefault = e.target.value;
                      setDefaultRole(newDefault);
                      // Update existing teachers that still match the previous default role
                      setParsedTeachers((prev) =>
                        prev.map((t) =>
                          t.role === defaultRole ? { ...t, role: newDefault } : t
                        )
                      );
                    }}
                    size="sm"
                  >
                    {countryFilteredRoles.map((role) => (
                      <option key={role.id || role.name} value={role.name}>
                        {role.name}
                      </option>
                    ))}
                  </Form.Select>
                  {countryFilteredRoles.length === 0 && roleDefs.length > 0 && (
                    <Form.Text className="text-muted small">
                      No roles available for this country
                    </Form.Text>
                  )}
                </Form.Group>

                {/* Inviter Selection */}
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Requested By *</Form.Label>
                  <Form.Select
                    value={selectedInviter}
                    onChange={(e) => setSelectedInviter(e.target.value)}
                    size="sm"
                    required
                  >
                    <option value="ALPACA">ALPACA (System Default)</option>
                    {availableTeachers.length > 0 && (
                      <>
                        <option disabled>──────────</option>
                        {availableTeachers.map((teacher) => (
                          <option key={teacher.id} value={teacher.id}>
                            {teacher.name ||
                              `${teacher.firstName ?? ""} ${teacher.lastName ?? ""}`.trim()}
                          </option>
                        ))}
                      </>
                    )}
                  </Form.Select>
                  <Form.Text className="text-muted small">
                    {selectedInviter === "ALPACA" 
                      ? "Invitations will be sent from the ALPACA system"
                      : "Who is requesting these teacher invitations?"}
                  </Form.Text>
                </Form.Group>
              </div>

              {/* Bulk Import Section */}
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="text-primary mb-2">💡 Bulk Import - Save Time!</h6>
                <p className="small text-muted mb-2">
                  Adding multiple teachers? Copy and paste them all at once.
                </p>

                {/* Parse Mode Selection */}
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Format</Form.Label>
                  <Form.Select
                    value={parseMode}
                    onChange={(e) => setParseMode(e.target.value)}
                    size="sm"
                  >
                    <option value="newline">One teacher per line</option>
                    <option value="comma">Comma separated</option>
                    <option value="tab">Tab separated</option>
                  </Form.Select>
                </Form.Group>

                {/* Text Area */}
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">
                    Paste Teacher List{" "}
                    <Badge bg="secondary" className="ms-1">
                      Name &amp; Email Required
                    </Badge>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder={`Example - try copying and pasting this:

John Smith - john.smith@school.ie
Mary Johnson (mary.j@school.ie)
Peter O'Connor - peter.oconnor@school.ie
sarah.brown@school.ie Sarah Brown`}
                    value={rawTeacherText}
                    onChange={(e) => setRawTeacherText(e.target.value)}
                    className="font-monospace"
                    style={{ fontSize: "0.9rem" }}
                  />
                </Form.Group>
                <Form.Text className="text-muted small">
                  ✅ Automatically detects names and emails
                </Form.Text>
              </div>

              {/* Status */}
              {parsedTeachers.length > 0 && (
                <div className="text-center">
                  <Badge
                    bg={validTeachersCount > 0 ? "success" : "warning"}
                    className="p-2"
                  >
                    📊 {parsedTeachers.length} found • {validTeachersCount} valid
                  </Badge>
                </div>
              )}
            </div>
          </Col>

          {/* Column 2: Teachers Table */}
          <Col md={8}>
            <div className="mb-3">
              <h5 className="mb-3">
                Teachers to Add
                <Badge bg="primary" className="ms-2">
                  {validTeachersCount}
                </Badge>
              </h5>

              <div style={{ height: "550px", overflowY: "auto" }}>
                {parsedTeachers.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <FaUser size={48} className="mb-3 opacity-50" />
                    <h6>No Teachers Added Yet</h6>
                    <p className="mb-2">Get started by:</p>
                    <div className="text-start d-inline-block">
                      <p className="mb-1">
                        • Clicking <strong>"Add One"</strong> for single entry
                      </p>
                      <p className="mb-1">
                        • Using <strong>"Bulk Import"</strong> to paste multiple
                      </p>
                    </div>
                    <small className="text-muted">
                      Name, email, and role are required for each teacher
                    </small>
                  </div>
                ) : (
                  <Table striped bordered hover size="sm">
                    <thead className="sticky-top bg-light">
                      <tr>
                        <th style={{ width: "200px" }}>First Name</th>
                        <th style={{ width: "200px" }}>Last Name</th>
                        <th style={{ width: "250px" }}>Email</th>
                        <th style={{ width: "150px" }}>Role</th>
                        <th style={{ width: "200px" }}>Class (Optional)</th>
                        <th style={{ width: "60px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedTeachers.map((teacher) => (
                        <tr
                          key={teacher.id}
                          className={!teacher.isValid ? "table-warning" : ""}
                        >
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="First name *"
                              value={teacher.firstName}
                              onChange={(e) =>
                                updateTeacherField(
                                  teacher.id,
                                  "firstName",
                                  e.target.value
                                )
                              }
                              isInvalid={
                                !teacher.isValid && !teacher.firstName.trim()
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="Last name"
                              value={teacher.lastName}
                              onChange={(e) =>
                                updateTeacherField(
                                  teacher.id,
                                  "lastName",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="email"
                              placeholder="email@school.ie *"
                              value={teacher.email}
                              onChange={(e) =>
                                updateTeacherField(
                                  teacher.id,
                                  "email",
                                  e.target.value
                                )
                              }
                              isInvalid={
                                !teacher.isValid &&
                                (!teacher.email ||
                                  !/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(
                                    teacher.email
                                  ))
                              }
                            />
                            {!teacher.isValid && teacher.validationError && (
                              <small className="text-danger d-block mt-1">
                                {teacher.validationError}
                              </small>
                            )}
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={teacher.role}
                              onChange={(e) =>
                                updateTeacherField(
                                  teacher.id,
                                  "role",
                                  e.target.value
                                )
                              }
                            >
                              {countryFilteredRoles.map((role) => (
                                <option
                                  key={role.id || role.name}
                                  value={role.name}
                                >
                                  {role.name}
                                </option>
                              ))}
                            </Form.Select>
                            {countryFilteredRoles.length === 0 && (
                              <small className="text-muted d-block mt-1">
                                No roles for country
                              </small>
                            )}
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={teacher.sharedClassId || ""}
                              onChange={(e) =>
                                updateTeacherField(
                                  teacher.id,
                                  "sharedClassId",
                                  e.target.value || null
                                )
                              }
                            >
                              <option value="">No class</option>
                              {academicYearFilteredClasses.map((classItem) => (
                                <option key={classItem.id} value={classItem.id}>
                                  {classItem.className || classItem.name}
                                </option>
                              ))}
                            </Form.Select>
                            {academicYearFilteredClasses.length === 0 &&
                              availableClasses.length > 0 && (
                                <small className="text-muted d-block mt-1">
                                  No classes for {ayShort}
                                </small>
                              )}
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeTeacher(teacher.id)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {validTeachersCount > 0 && (
              <span>
                <FaGraduationCap className="me-1" />
                Adding {validTeachersCount} teacher(s) as pending colleagues
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddTeachers}
              disabled={validTeachersCount === 0 || loading}
            >
              {loading
                ? "Adding Teachers..."
                : `Add ${validTeachersCount} Teacher(s)`}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddTeachersModal;